// react-i18next setup. Called once per app (apps/sell, apps/shop) from
// main.tsx before <App /> mounts.
//
// Design:
//   * English catalog is bundled (imported statically) so first paint
//     never blocks on a network request.
//   * Other locales are dynamically imported (Vite code-splits each as
//     its own chunk). The chunk loads on language change.
//   * Initial language: `core.users.preferred_locale` (when authenticated)
//     > localStorage > navigator.language > 'en'. The DB read happens
//     after auth bootstrap; we update i18n.language() then.
//   * <html lang> + <html dir> are kept in sync so screen readers and
//     CSS logical properties get the right values.

import i18next, { type i18n, type Resource } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LOCALE, normalizeLocale, isRtl, type SupportedLocale } from './locales';
import enCommon from './locales/en/common.json';

// All non-English catalogs are loaded on demand via dynamic import. Vite
// resolves these at build time so each becomes its own JS chunk.
const LAZY_CATALOGS: Record<Exclude<SupportedLocale, 'en'>, () => Promise<Resource>> = {
  es: async () => ({ common: (await import('./locales/es/common.json')).default }),
  'zh-CN': async () => ({ common: (await import('./locales/zh-CN/common.json')).default }),
  'zh-TW': async () => ({ common: (await import('./locales/zh-TW/common.json')).default }),
  ja: async () => ({ common: (await import('./locales/ja/common.json')).default }),
  ko: async () => ({ common: (await import('./locales/ko/common.json')).default }),
  vi: async () => ({ common: (await import('./locales/vi/common.json')).default }),
  fr: async () => ({ common: (await import('./locales/fr/common.json')).default }),
  de: async () => ({ common: (await import('./locales/de/common.json')).default }),
  'pt-BR': async () => ({ common: (await import('./locales/pt-BR/common.json')).default }),
  ar: async () => ({ common: (await import('./locales/ar/common.json')).default }),
  hi: async () => ({ common: (await import('./locales/hi/common.json')).default }),
  ru: async () => ({ common: (await import('./locales/ru/common.json')).default }),
  it: async () => ({ common: (await import('./locales/it/common.json')).default }),
  th: async () => ({ common: (await import('./locales/th/common.json')).default }),
};

let initialized = false;

export interface InitI18nOptions {
  /** Which app — used to namespace the localStorage key. */
  app: 'sell' | 'shop' | 'web' | 'admin' | 'auth' | 'wholesale' | 'fulfillment';
  /** Override the initial language (e.g. from core.users.preferred_locale). */
  initialLocale?: SupportedLocale | null;
  /**
   * Disable the language detector entirely (e.g. for SSR or tests where
   * we want a deterministic initial locale).
   */
  disableDetector?: boolean;
}

export function initI18n(opts: InitI18nOptions): i18n {
  if (initialized) return i18next;
  initialized = true;

  const storageKey = `pc.i18n.${opts.app}.lang`;
  const initialLanguage = opts.initialLocale
    ? normalizeLocale(opts.initialLocale)
    : undefined;

  const chain = i18next;
  if (!opts.disableDetector) {
    chain.use(LanguageDetector);
  }
  // Dev-mode missing-key visibility. In dev, every `t('foo.bar')` lookup
  // that resolves to the source value (i.e. the requested locale has no
  // entry for that key OR the key doesn't exist at all) prints a single
  // console.warn so the developer sees instantly which keys are missing
  // for the currently-selected locale. Silent in prod — these are noisy
  // by design.
  //
  // To audit a page: open it, set the language picker to a non-English
  // locale, and scroll through the page. Every warning is one missing
  // catalog entry to add and run `pnpm --filter @pc/i18n run translate`
  // to fan out to all 14 locales. See ai-rules/i18n-no-hardcoded.md.
  const isDev =
    typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

  chain.use(initReactI18next).init({
    resources: {
      en: { common: enCommon },
    },
    saveMissing: isDev,
    missingKeyHandler: isDev
      ? (lngs, ns, key, fallback) => {
          // Don't warn for English — that's the source of truth; a "miss"
          // there means the key doesn't exist anywhere, which is a code
          // bug, not a translation gap. Surface it loudly.
          const lng = Array.isArray(lngs) ? lngs[0] : lngs;
          if (lng === DEFAULT_LOCALE) {
            // eslint-disable-next-line no-console
            console.error(
              `[i18n] missing key in source catalog: "${String(ns)}:${String(key)}" — add to packages/i18n/src/locales/en/common.json then run \`pnpm --filter @pc/i18n run translate\`.`,
            );
            return;
          }
          // eslint-disable-next-line no-console
          console.warn(
            `[i18n] missing "${String(ns)}:${String(key)}" for locale "${String(lng)}" — falling back to "${String(fallback)}". Run \`pnpm --filter @pc/i18n run translate\` to populate.`,
          );
        }
      : undefined,
    lng: initialLanguage,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [
      'en',
      'es',
      'zh-CN',
      'zh-TW',
      'ja',
      'ko',
      'vi',
      'fr',
      'de',
      'pt-BR',
      'ar',
      'hi',
      'ru',
      'it',
      'th',
    ],
    // NOTE: do NOT set `nonExplicitSupportedLngs: true`. With this flag on,
    // i18next strips the region tag during resolution (`zh-CN` -> `zh`,
    // `pt-BR` -> `pt`) and then rejects the resolved code because the bare
    // language is not listed in `supportedLngs`. Result: every region-tagged
    // locale silently falls back to English while bare codes (`es`, `ja`,
    // ...) work. Region tag normalization is handled instead by
    // `convertDetectedLanguage` below + `normalizeLocale` in setLanguage().
    load: 'currentOnly',
    ns: ['common'],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    // bindI18nStore is CRITICAL: non-English catalogs load lazily (see
    // LAZY_CATALOGS), so on a hard reload the first render usually beats
    // the catalog chunk. react-i18next's default bindI18nStore is '' —
    // meaning components do NOT re-render when addResourceBundle() lands —
    // so every mounted component silently stayed English until the next
    // navigation or language change. Binding 'added' re-renders the tree
    // the moment the catalog registers.
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: storageKey,
      // navigator.language may be `en-US`, `zh-Hant-HK`, `pt`, etc. Map to
      // one of SUPPORTED_LOCALES before i18next applies its support filter.
      convertDetectedLanguage: (lng: string) => normalizeLocale(lng),
    },
  });

  // Keep <html lang> + <html dir> in sync with i18next.language.
  if (typeof document !== 'undefined') {
    const applyHtml = (lng: string) => {
      const tag = normalizeLocale(lng);
      document.documentElement.setAttribute('lang', tag);
      document.documentElement.setAttribute('dir', isRtl(tag) ? 'rtl' : 'ltr');
    };
    applyHtml(i18next.language);
    i18next.on('languageChanged', (lng) => {
      void ensureLoaded(lng).then(() => applyHtml(lng));
    });
  }

  // Fire-and-forget: load the initial language's catalog so the first paint
  // doesn't show English fallbacks for a non-English user.
  void ensureLoaded(i18next.language);

  return i18next;
}

/**
 * Load the bundle for a given locale if it hasn't been loaded yet.
 * Returns when the bundle is registered (or immediately when no-op).
 */
export async function ensureLoaded(localeRaw: string): Promise<void> {
  const locale = normalizeLocale(localeRaw);
  if (locale === 'en') return;
  if (i18next.hasResourceBundle(locale, 'common')) return;
  const loader = LAZY_CATALOGS[locale];
  if (!loader) return;
  const resource = await loader();
  for (const [ns, table] of Object.entries(resource)) {
    i18next.addResourceBundle(locale, ns, table, true, true);
  }
}

/** Switch language (loads the catalog first if missing). */
export async function setLanguage(localeRaw: string): Promise<void> {
  const locale = normalizeLocale(localeRaw);
  await ensureLoaded(locale);
  await i18next.changeLanguage(locale);
}

/** Read the current active language as a SupportedLocale. */
export function currentLocale(): SupportedLocale {
  return normalizeLocale(i18next.language);
}

export { i18next };
